<script>
import getUuid from 'widget/helpers/uuid';
import BranchContent from './BranchContent.vue';
import Draggable from 'vuedraggable';

import ButtonNext from 'next/button/Button.vue';

export default {
  components: {
    Draggable,
    BranchContent,
    ButtonNext,
  },
  props: {
    step: {
      type: Object,
      default: () => {},
    },
    variables: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['stepChange'],
  data() {
    return {
      fallbackName: this.$t(
        'WGPT_AUTOMATIONS.EDITOR.BRANCH.FALLBACK_CONDITION_NAME'
      ),
      updatedName: this.step.name,
      isEditing: false,
      dragOptions: {
        animation: 200,
        preventOnFilter: false,
        filter: '.ignore-drag',
      },
    };
  },
  computed: {
    stepName() {
      return this.step.name || this.fallbackName;
    },
    branches: {
      get() {
        return this.step.branches || [];
      },
      set(data) {
        this.$emit('stepChange', this.step.id, { branches: data });
      },
    },
    disabledRemoveBranch() {
      if (this.disabled) return true;
      return this.branches.length <= 1;
    },
    enabled() {
      return !this.disabled;
    },
  },
  watch: {
    step(step, stepBefore) {
      if (step.id !== stepBefore.id || step.name !== this.updatedName) {
        this.updatedName = step.name;
      }
    },
  },
  methods: {
    onBranchChange(branchId, value) {
      const branchesBefore = this.branches;
      const branches = branchesBefore.map(branchBefore => {
        if (branchBefore.id !== branchId) return branchBefore;
        return {
          ...branchBefore,
          ...value,
        };
      });
      const stepUpdates = {
        branches,
      };
      this.$emit('stepChange', this.step.id, stepUpdates);
    },
    addBranch() {
      const branchesBefore = this.branches;
      const branch = {
        id: getUuid(),
        name: this.$t('WGPT_AUTOMATIONS.EDITOR.BRANCH.FALLBACK_BRANCH_NAME', {
          index: branchesBefore.length + 1,
        }),
        logical_operator: 'AND',
        conditions: [
          {
            variable_name: '',
            operator: 'equals',
            value: '',
          },
        ],
        steps: [],
      };
      const branches = branchesBefore.concat(branch);
      const stepUpdates = {
        branches,
      };
      this.$emit('stepChange', this.step.id, stepUpdates);
    },
    removeBranch(branchId) {
      const branchesBefore = this.branches;
      const branches = branchesBefore.filter(
        branchBefore => branchBefore.id !== branchId
      );
      const stepUpdates = {
        branches,
      };
      this.$emit('stepChange', this.step.id, stepUpdates);
    },
    onBranchMoveUp(index) {
      const branches = this.branches;
      const branchToMove = branches.splice(index, 1)[0];
      branches.splice(index - 1, 0, branchToMove);
      const stepUpdates = {
        branches,
      };
      this.$emit('stepChange', this.step.id, stepUpdates);
    },
    onBranchMoveDown(index) {
      const branches = this.branches;
      const branchToMove = branches.splice(index, 1)[0];
      branches.splice(index + 1, 0, branchToMove);
      const stepUpdates = {
        branches,
      };
      this.$emit('stepChange', this.step.id, stepUpdates);
    },
    editName() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.$refs.nameInput.focus();
      });
    },
    onNameChange() {
      const stepUpdates = {
        name: this.updatedName,
      };
      this.$emit('stepChange', this.step.id, stepUpdates);
      this.isEditing = false;
      this.$refs.nameInput.blur();
    },
    cancelNameEditing() {
      this.isEditing = false;
    },
  },
};
</script>

<template>
  <div class="p-6 flex flex-col gap-6">
    <header class="group pe-10 flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <input
          v-show="isEditing"
          ref="nameInput"
          v-model.trim="updatedName"
          :placeholder="fallbackName"
          class="name-input ignore-drag !ps-1 !bg-n-alpha-black2 disabled:!cursor-text !text-slate-900 dark:!text-slate-100 !border-none !outline !outline-2 !outline-n-blue-border -outline-offset-2 !m-0 text-xl !font-semibold !h-6"
          dir="auto"
          type="text"
          @blur="onNameChange"
          @keyup.enter="onNameChange"
          @keyup.esc="cancelNameEditing"
        />
        <h5
          v-if="!isEditing"
          class="grow text-base font-semibold text-black-900 dark:text-slate-200 select-none inline-flex items-center gap-1.5 overflow-hidden"
        >
          <span class="truncate">{{ stepName }}</span>
          <ButtonNext
            color="slate"
            size="xs"
            variant="ghost"
            icon="i-lucide-pencil"
            class="invisible group-hover:visible ignore-drag"
            @click="editName"
          />
        </h5>
      </div>
      <p class="mb-0 text-xs text-n-slate-11">
        <span>
          {{ $t('WGPT_AUTOMATIONS.REGISTRY.ACTIONS.BRANCH.NAME') }}
        </span>
      </p>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ $t('WGPT_AUTOMATIONS.REGISTRY.ACTIONS.BRANCH.DESCRIPTION') }}
      </p>
    </header>

    <div class="border-t border-n-weak" />

    <div>
      <Draggable
        v-model="branches"
        item-key="id"
        v-bind="dragOptions"
        force-fallback
        dir="ltr"
        :disabled="disabled"
        class="flex flex-col gap-4"
      >
        <template #item="{ element: branch, index }">
          <BranchContent
            :branch="branch"
            :variables="variables"
            :index="index"
            :is-first="index === 0"
            :is-last="index === branches.length - 1"
            :disable-remove="disabledRemoveBranch"
            :disabled="disabled"
            :dirty="dirty"
            @branch-change="e => onBranchChange(branch.id, e)"
            @remove="removeBranch(branch.id)"
            @move-up="onBranchMoveUp(index)"
            @move-down="onBranchMoveDown(index)"
          />
        </template>
      </Draggable>
    </div>

    <ButtonNext
      v-if="enabled"
      color="slate"
      variant="faded"
      icon="i-lucide-plus"
      @click="addBranch"
    >
      {{ $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.BRANCH.ADD_BRANCH_BUTTON_TEXT') }}
    </ButtonNext>
  </div>
</template>
